// "Parse Content" support: identifies all input fields/placeholders and ensures editability
// while preserving 100% exact original positioning, dimensions, styles, and template layout.
import { FormDocument, FormElement, FormPage } from '../store/types';

interface ElementChange {
  id: string;
  changes: Partial<FormElement>;
}

export function computeAutoFormattedPages(doc: FormDocument): FormPage[] {
  return doc.pages.map((page) => {
    let updatedElements: FormElement[] = page.elements.map((el) => {
      // 1. If it is a detected input field box (rect with input dimensions <= 40px height), convert to editable textbox
      const isInputBox = el.type === 'rect' && el.height <= 40.0 && el.width >= 12.0 && el.height >= 5.0;
      if (isInputBox) {
        return {
          ...el,
          type: 'textbox',
          locked: false,
          bindings: el.bindings || {},
          style: {
            ...el.style,
            background: el.style.background && el.style.background !== 'transparent' ? el.style.background : '#ffffff',
            borderWidth: el.style.borderWidth || 1,
            borderStyle: el.style.borderStyle || 'solid',
            borderColor: el.style.borderColor || '#cbd5e1',
            borderRadius: 4,
            padding: 0,
          },
        };
      }

      // 2. Text, labels, headings, paragraphs, and input types are all editable
      if (
        el.type === 'text' ||
        el.type === 'label' ||
        el.type === 'heading' ||
        el.type === 'paragraph' ||
        el.type === 'textbox' ||
        el.type === 'textarea' ||
        el.type === 'date' ||
        el.type === 'checkbox' ||
        el.type === 'radio' ||
        el.type === 'dropdown' ||
        el.type === 'table'
      ) {
        return {
          ...el,
          locked: false,
        };
      }

      // 3. Background lines and section container outlines stay locked
      return {
        ...el,
        locked: true,
      };
    });

    // 4. Auto-Space Layout Optimization Formula: eliminate empty dead space between labels & boxes
    const rows: FormElement[][] = [];
    const sorted = [...updatedElements].sort((a, b) => a.y - b.y || a.x - b.x);

    for (const el of sorted) {
      if (el.type === 'rect' || el.type === 'hline' || el.type === 'vline') continue;
      let matchedRow: FormElement[] | null = null;
      for (const r of rows) {
        const avgY = r.reduce((acc, item) => acc + item.y, 0) / r.length;
        if (Math.abs(el.y - avgY) <= 6) {
          matchedRow = r;
          break;
        }
      }
      if (matchedRow) {
        matchedRow.push(el);
      } else {
        rows.push([el]);
      }
    }

    const modifiedMap = new Map<string, Partial<FormElement>>();

    for (const r of rows) {
      r.sort((a, b) => a.x - b.x);
      for (let i = 0; i < r.length - 1; i++) {
        const curr = r[i];
        const nxt = r[i + 1];
        if (
          (curr.type === 'label' || curr.type === 'text') &&
          (nxt.type === 'textbox' || nxt.type === 'textarea' || nxt.type === 'date' || nxt.type === 'checkbox')
        ) {
          const currRight = curr.x + curr.width;
          const gap = nxt.x - currRight;
          if (gap > 12.0) {
            const savedSpace = gap - 6.0;
            const newX = Math.round((currRight + 6.0) * 10) / 10;
            const newW = Math.round((nxt.width + savedSpace) * 10) / 10;
            modifiedMap.set(nxt.id, { x: newX, width: newW });
          }
        }
      }
    }

    if (modifiedMap.size > 0) {
      updatedElements = updatedElements.map((el) => {
        const mod = modifiedMap.get(el.id);
        return mod ? { ...el, ...mod } : el;
      });
    }

    return {
      ...page,
      elements: updatedElements,
    };
  });
}

export function computeAutoFormatChanges(doc: FormDocument): { pageId: string; changes: ElementChange[] }[] {
  const formattedPages = computeAutoFormattedPages(doc);
  return formattedPages
    .map((page) => ({
      pageId: page.id,
      changes: page.elements.map((el) => ({
        id: el.id,
        changes: {
          type: el.type,
          locked: el.locked,
          bindings: el.bindings,
          style: el.style,
        },
      })),
    }))
    .filter((p) => p.changes.length > 0);
}
